import { ReactElement, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Breadcrumbs,
  Error,
  Prompt,
  Success,
  Tooltip,
  Typography,
  SnackbarStatus,
  useSnackbar,
  CardGrid,
  EllipsisMenu,
  ExpandToggle,
  ArrowDownIcon,
  Bin,
  ArrowUpIcon,
  EditIcon,
  AddPriceBandIcon,
  PriceBandIcon,
  Chip,
  AvatarGroup,
  Plus,
  SoftButton,
  ChannelIcon,
  AttentionIcon,
} from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import { useQueryClient } from '@tanstack/react-query';
import styles from './MenuSection.module.scss';
import MenuItemCardSkeleton from 'src/components/MenuItemCardSkeleton';
import { moveSection } from 'src/api/moveSection/moveSection';
import { ISection } from 'src/types/menu.types';
import { deleteSection } from 'src/api/deleteSection/deleteSection';
import { getHierarchyNames } from 'src/helpers';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { getPromptText } from './utilities/getPromptText';
import MenuItemCard from '../MenuItemCard';
import { ICategoryWithChildren } from 'src/components/MenuSectionItems/utilities';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { isSectionRestricted, resolveSectionAvailability } from 'src/helpers/sectionAvailability/sectionAvailability';

const NO_AVAILABILITY_MESSAGE =
  "This section has no channel availability and won't be published to any channel. Manage its availability to choose where it should appear.";

export interface ISectionTrayTarget {
  parentSectionId?: string | null;
  section?: ICategoryWithChildren;
}

export interface ISiblingDisplayOrder {
  up?: number;
  down?: number;
}

export interface IPendingAddition {
  sectionId: string | null;
  count: number;
}

export interface IMenuSectionProps {
  menuId: string;
  section: ICategoryWithChildren;
  handleSectionModal?: (options?: ISectionTrayTarget) => void;
  handleManageAvailabilityModal?: (section: ICategoryWithChildren) => void;
  onClickNewItem: (sectionId?: string) => void;
  displayOrder?: ISiblingDisplayOrder;
  isAssignedToMealtime: boolean | undefined;
  tooltipPosition: 'top' | 'bottom';
  sections: ISection[];
  children?: ReactNode;
  isAddProductsHidden?: boolean;
  pendingAddition?: IPendingAddition;
  variant?: 'default' | 'unassigned';
  scrollToSectionId?: string | null;
  onScrolledIntoView?: () => void;
}
const cx = classnames.bind(styles);
const MenuSection = ({
  menuId,
  handleSectionModal,
  handleManageAvailabilityModal,
  onClickNewItem,
  section,
  displayOrder,
  isAssignedToMealtime,
  tooltipPosition,
  sections,
  children,
  isAddProductsHidden,
  pendingAddition,
  variant = 'default',
  scrollToSectionId,
  onScrolledIntoView,
}: IMenuSectionProps): ReactElement => {
  const isUnassigned = variant === 'unassigned';
  const { sectionId, name, description, items, parentSectionId, children: subsections, venueCount } = section;
  const sectionCardRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const isRestricted = isSectionRestricted(section);
  const resolvedAvailability = resolveSectionAvailability(section, sections);
  const isSectionUnavailable =
    resolvedAvailability.instanceIds !== null && resolvedAvailability.instanceIds.length === 0;
  const channels = isRestricted ? section.channels ?? [] : [];
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const removePromptTitle = `Are you sure you want to delete "${name}" ${
    parentSectionId ? 'sub-section' : 'menu section'
  }?`;
  const removeSectionPromptSubTitle = getPromptText(name, items, subsections);
  const categoryBreadcrumbs = useMemo(
    () =>
      getHierarchyNames(section, sections, { id: 'sectionId', parent_id: 'parentSectionId' }).map((name) => ({
        label: name,
      })),
    [section, sections],
  );

  useEffect(() => {
    if (scrollToSectionId && scrollToSectionId === sectionId) {
      sectionCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onScrolledIntoView?.();
    }
  }, [scrollToSectionId, sectionId, onScrolledIntoView]);

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await deleteSection(menuId, sectionId);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (e) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We were unable to delete, please try again!',
      });
      setIsDeleteLoading(false);
    }
  };

  const reorderSection = async (newDisplayOrder: number) => {
    try {
      showSnackbar({
        status: SnackbarStatus.PROGRESS,
        message: 'Moving menu section',
      });
      await moveSection({ menuId, sectionId, newDisplayOrder });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      hideSnackbar();
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  const pendingCount = pendingAddition?.sectionId === (sectionId || null) ? pendingAddition.count : 0;

  const itemCards = [
    ...items.map((item, index) => (
      <MenuItemCard
        {...item}
        menuId={menuId}
        itemId={item.itemId}
        parentItemId={item.parentItemId}
        sectionId={sectionId}
        displayOrderLeft={items[index - 1]?.displayOrder}
        displayOrderRight={items[index + 1]?.displayOrder}
        key={item.itemId}
      />
    )),
    ...Array.from({ length: pendingCount }, (_, index) => <MenuItemCardSkeleton key={`pending-${index}`} />),
    !isAddProductsHidden && (
      <SoftButton
        colour="blueberry"
        icon={Plus}
        size="square"
        className={styles['add-product']}
        onClick={() => onClickNewItem(sectionId)}
        key="add"
      >
        Add products
      </SoftButton>
    ),
  ];

  const ellipsisMenu = (
    <EllipsisMenu
      disabled={isAddProductsHidden}
      options={[
        {
          id: 0,
          name: 'Edit',
          icon: EditIcon,
          onOptionClick: () => handleSectionModal?.({ section }),
          dataTestId: `rename-section-${name}`,
        },
        {
          id: 'manage-availability',
          name: 'Manage availability',
          icon: ChannelIcon,
          onOptionClick: () => handleManageAvailabilityModal?.(section),
          dataTestId: `manage-availability-section-${name}`,
        },
        {
          id: 1,
          name: 'Move up',
          icon: ArrowUpIcon,
          onOptionClick: () => reorderSection(displayOrder?.up),
          isDisabled: displayOrder?.up === undefined,
          dataTestId: `move-up-section-${name}`,
        },
        {
          id: 2,
          name: 'Move down',
          icon: ArrowDownIcon,
          onOptionClick: () => reorderSection(displayOrder?.down),
          isDisabled: displayOrder?.down === undefined,
          dataTestId: `move-down-section-${name}`,
        },
        {
          id: 'd-1',
          isDivider: true,
        },
        {
          id: 'add-category',
          name: 'Add section',
          icon: AddPriceBandIcon,
          onOptionClick: () => handleSectionModal?.({ parentSectionId: null }),
        },
        {
          id: 'add-subcategory',
          name: 'Add subsection',
          icon: PriceBandIcon,
          onOptionClick: () => handleSectionModal?.({ parentSectionId: sectionId }),
        },
        {
          id: 'd-2',
          isDivider: true,
        },
        {
          id: 3,
          name: 'Delete',
          icon: Bin,
          onOptionClick: () => setIsRemovePromptVisible(true),
          hoverColour: 'tomato',
          dataTestId: `delete-section-${name}`,
        },
      ]}
      variant="ghost"
    />
  );

  const productCount = items.length;
  const subsectionCount = subsections.length;
  const hasChannels = !!channels?.length;
  const isEmptyWithoutAddButton = productCount === 0 && pendingCount === 0 && subsectionCount === 0;
  const isExpandToggleDisabled = isAddProductsHidden && isEmptyWithoutAddButton;
  const isSectionExpanded = isExpandToggleDisabled ? false : isExpanded;

  return (
    <div
      ref={sectionCardRef}
      className={cx('section-card', {
        'section-card-unassigned': isUnassigned,
        'section-card-top-level': !isUnassigned && !parentSectionId,
      })}
      data-testid={`${name}-section-divider`}
    >
      {!isUnassigned && (
        <Prompt
          promptHeading={removePromptTitle}
          closePrompt={() => setIsRemovePromptVisible(false)}
          open={isRemovePromptVisible}
          size="small"
          backButton={true}
          backButtonText="Back"
          buttonText="Delete"
          buttonColour="tomato"
          buttonType="button"
          buttonHandler={handleDelete}
          isSubmitting={isDeleteLoading}
        >
          <Typography>{removeSectionPromptSubTitle}</Typography>
        </Prompt>
      )}
      <div className={styles['header']}>
        <div className={styles['header-main']}>
          <div className={styles['title-row']}>
            {isUnassigned ? (
              <Typography size="large" weight="bold">
                {name}
              </Typography>
            ) : (
              <Breadcrumbs breadcrumbs={categoryBreadcrumbs} maxLevel={2} size="large" boldLast />
            )}
            <ExpandToggle
              isExpanded={isSectionExpanded}
              onClick={() => !isExpandToggleDisabled && setIsExpanded((prev) => !prev)}
              size="small"
              className={cx('expand-toggle', { 'expand-toggle-disabled': isExpandToggleDisabled })}
            />
            {isAssignedToMealtime !== undefined && !parentSectionId && (
              <Tooltip
                text={
                  isAssignedToMealtime
                    ? 'This menu section has been assigned to a mealtime and is ready to be published.'
                    : 'This menu section is not assigned to a mealtime, and will not be published when using mealtimes.'
                }
                position={tooltipPosition}
              >
                {isAssignedToMealtime ? <Success className={styles['kale']} /> : <Error className={styles['tomato']} />}
              </Tooltip>
            )}
          </div>
          {description && <Typography className={styles['description']}>{description}</Typography>}
          <div className={styles['meta-row']}>
            <Typography size="xsmall" className={productCount === 0 ? styles['tomato'] : undefined}>
              {productCount} {pluralize('Product', productCount)}
            </Typography>
            {subsectionCount > 0 && (
              <>
                <Typography size="xsmall">•</Typography>
                <Typography size="xsmall">
                  {subsectionCount} Sub-section{subsectionCount === 1 ? '' : 's'}
                </Typography>
              </>
            )}
            {(hasChannels || isSectionUnavailable) && (
              <>
                <Typography size="xsmall">•</Typography>
                <div className={styles['channels']}>
                  <Typography size="xsmall">Channels -</Typography>
                  <Tooltip
                    text={
                      isSectionUnavailable
                        ? NO_AVAILABILITY_MESSAGE
                        : channels.map((channel) => channel.name).join(', ')
                    }
                  >
                    <div className={styles['channels-avatars']}>
                      {hasChannels && (
                        <AvatarGroup
                          max={3}
                          avatars={channels.map((channel) => ({
                            variant: 'custom',
                            imageUrl: channel.faviconUrl,
                            brand: channel.name,
                            initials: channel.faviconUrl ? undefined : channel.name.slice(0, 2).toUpperCase(),
                          }))}
                        />
                      )}
                      {isSectionUnavailable && <AttentionIcon className={styles['tomato']} size="xsmall" />}
                    </div>
                  </Tooltip>
                </div>
              </>
            )}
            {venueCount !== undefined && (
              <>
                <Typography size="xsmall">•</Typography>
                <Typography size="xsmall">
                  Venues - <strong>{venueCount}</strong>
                </Typography>
              </>
            )}
          </div>
        </div>
        <div className={styles['header-actions']}>
          {isUnassigned ? (
            <Chip label="Excluded" backgroundColour="tomato" />
          ) : (
            <>
              {isSectionUnavailable && (
                <Tooltip text={NO_AVAILABILITY_MESSAGE}>
                  <Chip label="No Availability" backgroundColour="tomato" icon="attention" />
                </Tooltip>
              )}
              {!isSectionUnavailable && isRestricted && <Chip label="Partial Availability" backgroundColour="grape" />}
              {ellipsisMenu}
            </>
          )}
        </div>
      </div>

      {isSectionExpanded && (
        <>
          <CardGrid data-cy="card-container">{itemCards}</CardGrid>
          {children}
        </>
      )}
    </div>
  );
};

export default MenuSection;
